import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '../../lib/api-fetcher';
import type {
  PidiScheme,
  PidiCollection,
  CreatePidiSchemeInput,
  RecordDailyCollectionInput,
  RequestWithdrawalInput,
} from './types';
import { multiplyMoney, subtractMoney } from '@repo/utils';

interface DepositAccountResponse {
  id: string;
  accountNumber: string;
  customerId: string;
  dailyAmountPaise: number;
  tenureDays: number;
  interestRateBps: number;
  startDate: string;
  maturityDate: string;
  status: 'APPLIED' | 'ACTIVE' | 'CLOSED';
  assignedAgentId?: string | null;
  balances?: {
    principalPaidPaise: number;
    interestAccruedPaise: number;
    totalBalancePaise: number;
  };
  allowedStatusTransitions?: string[];
  recentAccruals?: Array<{
    id: string;
    accrualDate: string;
    balancePaise: number;
    annualInterestBps: number;
    accruedPaise: number;
    createdAt: string;
  }>;
  withdrawals?: Array<{
    id: string;
    withdrawalType: 'PARTIAL' | 'PREMATURE_CLOSURE' | 'MATURITY_CLOSURE';
    amountPaise: number;
    principalAmountPaise: number;
    interestPaidPaise: number;
    interestForfeitedPaise: number;
    rail: string;
    status: 'REQUESTED' | 'APPROVED' | 'SETTLED' | 'CANCELLED';
    beneficiaryName: string;
    beneficiaryAccount: string;
    beneficiaryIfsc?: string | null;
    beneficiaryUpi?: string | null;
    requestedAt: string;
    approvedAt?: string | null;
    settledAt?: string | null;
  }>;
}

function mapAccountToScheme(acc: DepositAccountResponse): PidiScheme {
  return {
    id: acc.id,
    schemeNumber: acc.accountNumber,
    displayName: `Pidi Daily Deposit #${acc.accountNumber}`,
    accountNumber: acc.accountNumber,
    customerId: acc.customerId,
    dailyAmount: Math.round(acc.dailyAmountPaise / 100),
    dailyAmountPaise: acc.dailyAmountPaise,
    durationDays: acc.tenureDays,
    tenureDays: acc.tenureDays,
    interestRateBps: acc.interestRateBps,
    agentId: acc.assignedAgentId,
    agentName: acc.assignedAgentId ? `Agent ${acc.assignedAgentId.slice(0, 8)}` : 'Branch Staff',
    assignedAgentId: acc.assignedAgentId,
    status: acc.status,
    startDate: acc.startDate,
    endDate: acc.maturityDate,
    maturityDate: acc.maturityDate,
    collectedAmount: acc.balances?.principalPaidPaise ?? 0,
    pendingAmount: subtractMoney(multiplyMoney(acc.dailyAmountPaise, acc.tenureDays), acc.balances?.principalPaidPaise ?? 0).toNumber(),
    balances: acc.balances,
    recentAccruals: acc.recentAccruals,
    withdrawals: acc.withdrawals,
    allowedStatusTransitions: acc.allowedStatusTransitions,
  };
}

export function usePidiSchemes(filters?: { query?: string; status?: string }) {
  return useQuery({
    queryKey: ['pidi-schemes', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.query) params.append('query', filters.query);
      if (filters?.status) params.append('status', filters.status);
      const qs = params.toString();
      const res = await fetcher.get<{ data: DepositAccountResponse[]; meta: unknown }>(
        `/staff/v1/deposit-accounts${qs ? `?${qs}` : ''}`,
      );
      return {
        data: (res.data ?? []).map(mapAccountToScheme),
        meta: res.meta,
      };
    },
  });
}

export function useCreatePidiScheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePidiSchemeInput) =>
      fetcher.post<{ data: DepositAccountResponse }>('/staff/v1/deposit-accounts', {
        customerId: body.customerId,
        dailyAmountPaise: body.dailyAmountPaise,
        tenureDays: body.tenureDays,
        interestRateBps: body.interestRateBps ?? 400,
        startDate: body.startDate,
        compoundingFrequency: body.compoundingFrequency ?? 'DAILY',
        assignedAgentId: body.assignedAgentId ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pidi-schemes'] });
    },
  });
}

export function usePidiCollections(filters?: { schemeNumber?: string; query?: string; status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['pidi-collections', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.schemeNumber) params.append('schemeNumber', filters.schemeNumber);
      if (filters?.query) params.append('query', filters.query);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      const qs = params.toString();
      return fetcher.get<{ data: PidiCollection[]; meta: { total: number; page: number; limit: number; offset: number } }>(
        `/staff/v1/deposit-accounts/collections${qs ? `?${qs}` : ''}`,
      );
    },
  });
}

export function useReversePidiCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return fetcher.post(`/staff/v1/finance/payments/receipts/${id}/reverse`, {
        reversalReason: 'Reversed by staff',
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pidi-collections'] });
      qc.invalidateQueries({ queryKey: ['pidi-schemes'] });
    },
  });
}

export function usePidiSchemeDetail(id: string) {
  return useQuery({
    queryKey: ['pidi-scheme', id],
    queryFn: async () => {
      const res = await fetcher.get<DepositAccountResponse>(`/staff/v1/deposit-accounts/${id}`);
      return res ? mapAccountToScheme(res) : null;
    },
    enabled: !!id,
  });
}

export function useUpdatePidiScheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CreatePidiSchemeInput> & { status?: string } }) =>
      fetcher.patch<unknown>(`/staff/v1/deposit-accounts/${id}`, body),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['pidi-schemes'] });
      qc.invalidateQueries({ queryKey: ['pidi-scheme', variables.id] });
    },
  });
}

export function useRecordPidiCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ depositAccountId, body }: { depositAccountId: string; body: RecordDailyCollectionInput }) =>
      fetcher.post(`/staff/v1/deposit-accounts/${depositAccountId}/collections`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pidi-schemes'] });
      qc.invalidateQueries({ queryKey: ['pidi-scheme'] });
    },
  });
}

export function useRequestPidiWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RequestWithdrawalInput) =>
      fetcher.post('/staff/v1/deposit-withdrawals', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pidi-schemes'] });
      qc.invalidateQueries({ queryKey: ['pidi-scheme'] });
    },
  });
}

